import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { TrendingUp, TrendingDown, Wallet, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function TradeDialog({ open, onOpenChange, asset, currentPrice, onTraded }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [side, setSide] = useState("buy");
  const [quantity, setQuantity] = useState("");
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      api.get("/paper/portfolio").then((r) => setPortfolio(r.data)).catch(() => {});
      setQuantity("");
      setSide("buy");
    }
  }, [open, user]);

  const total = parseFloat(quantity || 0) * (currentPrice || 0);
  const position = portfolio?.positions?.find(
    (p) => p.symbol === asset?.symbol && p.asset_type === asset?.asset_type
  );
  const ownedQty = position?.quantity || 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.info("Sign in to paper-trade");
      onOpenChange(false);
      navigate("/login");
      return;
    }
    const qty = parseFloat(quantity);
    if (!qty || qty <= 0) {
      toast.error("Enter a valid quantity");
      return;
    }
    setLoading(true);
    try {
      await api.post("/paper/trade", {
        symbol: asset.symbol,
        name: asset.name,
        asset_type: asset.asset_type,
        side,
        quantity: qty,
      });
      toast.success(`${side === "buy" ? "Bought" : "Sold"} ${qty} ${asset.symbol}`);
      onOpenChange(false);
      if (onTraded) onTraded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Trade failed");
    } finally {
      setLoading(false);
    }
  };

  const quickSet = (pct) => {
    if (!currentPrice || !portfolio) return;
    if (side === "buy") {
      const cash = portfolio.cash * pct;
      setQuantity((cash / currentPrice).toFixed(4));
    } else {
      setQuantity((ownedQty * pct).toFixed(4));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-md" data-testid="trade-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">
            Paper trade · {asset?.symbol}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#6B7068]">
            Practice trading with virtual cash. No real money involved.
          </DialogDescription>
        </DialogHeader>

        <div className="flex p-1 rounded-full bg-[#F9F8F6] border border-[#EAE6E1]" data-testid="trade-side-tabs">
          <button
            onClick={() => setSide("buy")}
            data-testid="trade-side-buy"
            className={`flex-1 py-2 rounded-full text-sm font-semibold transition-soft ${
              side === "buy" ? "bg-[#5C8D73] text-white" : "text-[#6B7068] hover:text-[#2A2F2B]"
            }`}
          >
            <TrendingUp className="w-4 h-4 inline mr-1.5" strokeWidth={2} /> Buy
          </button>
          <button
            onClick={() => setSide("sell")}
            data-testid="trade-side-sell"
            className={`flex-1 py-2 rounded-full text-sm font-semibold transition-soft ${
              side === "sell" ? "bg-[#C66B59] text-white" : "text-[#6B7068] hover:text-[#2A2F2B]"
            }`}
          >
            <TrendingDown className="w-4 h-4 inline mr-1.5" strokeWidth={2} /> Sell
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="flex justify-between text-sm">
            <span className="text-[#6B7068]">Market price</span>
            <span className="font-mono-ticker text-[#2A2F2B]">
              ${currentPrice ? currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : "—"}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[#6B7068]">{side === "buy" ? "Cash available" : "Shares owned"}</span>
            <span className="font-mono-ticker text-[#2A2F2B]">
              {side === "buy"
                ? `$${portfolio?.cash?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? "—"}`
                : ownedQty.toFixed(4)}
            </span>
          </div>

          <div>
            <Label htmlFor="qty" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7068]">
              Quantity
            </Label>
            <Input
              id="qty"
              type="number"
              step="any"
              min="0"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              data-testid="trade-quantity-input"
              className="mt-1.5 rounded-lg font-mono-ticker"
              placeholder="0.00"
              required
            />
            <div className="flex gap-2 mt-2">
              {[0.25, 0.5, 0.75, 1].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => quickSet(p)}
                  data-testid={`trade-quick-${p * 100}`}
                  className="flex-1 py-1.5 text-xs font-mono-ticker rounded-lg bg-[#F9F8F6] hover:bg-[#EAE6E1] text-[#6B7068] transition-soft"
                >
                  {p * 100}%
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-between items-baseline pt-2 border-t border-[#EAE6E1]">
            <span className="text-sm text-[#6B7068]">Estimated total</span>
            <span className="font-mono-ticker text-2xl text-[#2A2F2B]" data-testid="trade-total">
              ${total ? total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00"}
            </span>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="trade-submit-btn"
            className={`w-full rounded-full text-white py-6 transition-soft ${
              side === "buy" ? "bg-[#5C8D73] hover:bg-[#4a7460]" : "bg-[#C66B59] hover:bg-[#a85748]"
            }`}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <><Wallet className="w-4 h-4 mr-2" strokeWidth={1.5} /> {side === "buy" ? "Buy" : "Sell"} {asset?.symbol}</>
            )}
          </Button>
          <p className="text-[11px] text-center text-[#6B7068]">
            Paper trading uses virtual cash. Simulated fills at market price.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
